import { Box, CircularProgress, Pagination } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";

import CardBox from "../../components/card/card-box";
import ConfirmDialog from "../../components/dialogs/confirm";
import List from "../../components/list/list";
import ListItem from "../../components/list/list-item";
import useDeleteAsset from "../../hooks/mutation/assets/use-delete-asset";
import { CategoryContext } from "../../providers/category-provider";
import { FilesContext } from "../../providers/files-provider";
import { ListLayout } from "../../types/list";

export default function FilesList(): ReactElement {
  const [draggedID, setDraggedID] = useState<number | undefined | string>();
  const [cookies] = useCookies();
  const {
    limit,
    page,
    setPage,
    totalCount,
    isError,
    isLoading,
    assets,
    confirmationModal,
    setConfirmationModal,
    assetsChanged,
  } = useContext(FilesContext);

  const { categories } = useContext(CategoryContext);
  const { deleteAsset } = useDeleteAsset();

  useEffect(() => {
    if (!isError) {
      return;
    }

    enqueueSnackbar("Failed to load asset. Please try it again.", {
      variant: "error",
    });
  }, [isError]);

  function internalChangePage(value: number): void {
    setPage(value);
  }

  const handleDelete = async (): Promise<void> => {
    await deleteAsset(confirmationModal?.id as string);
    await assetsChanged();

    setConfirmationModal(undefined);
  };

  if (isLoading) {
    return (
      <Box className="flex w-full justify-center">
        <CircularProgress />
      </Box>
    );
  }

  if (assets.length === 0) {
    return (
      <Box className="flex w-full justify-center">
        <p>No Items</p>
      </Box>
    );
  }

  return (
    <>
      {cookies.listLayout === ListLayout.Grid ? (
        assets?.map((asset, index) => (
          <CardBox
            setConfirm={setConfirmationModal}
            getDradId={draggedID}
            setDragId={setDraggedID}
            isCheckbox
            draggable={true}
            key={asset.id}
            asset={asset}
            categories={categories}
            offset={index + 1}
          />
        ))
      ) : (
        <List>
          {assets?.map((asset, index) => (
            <ListItem
              setConfirm={setConfirmationModal}
              getDragId={draggedID}
              setDragId={setDraggedID}
              isCheckbox
              draggable={true}
              key={asset.id}
              asset={asset}
              offset={index + 1}
            />
          ))}
        </List>
      )}
      {Math.ceil(totalCount / limit) >= page && (
        <Pagination
          sx={{
            "& .MuiButtonBase-root.Mui-selected": {
              backgroundColor: "#4A4A4A",
              color: "white",
            },
          }}
          onChange={(_, page: number) => internalChangePage(page)}
          className="w-full flex justify-center"
          count={Math.ceil(totalCount / limit)}
          page={page}
          size="large"
        />
      )}
      {confirmationModal?.isOpen && (
        <ConfirmDialog
          header="Delete Asset"
          text="The Asset will be removed from the File-System. The Asset will be not available in the File-System anymore."
          isOpen={confirmationModal.isOpen}
          onClose={() => setConfirmationModal(undefined)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
}
